import type { AchievementPersistentEffects } from "@/features/achievements/achievementEffects";
import type { HovAmplifierEntryId } from "@/features/hoverIdle/hov/hovAmplifierModel";
import {
  composeMpmUnlockAdoption,
  type MpmUnlockIntent,
} from "../mpm/mpmUnlockAdoption";
import type { MpmState } from "../mpm/mpmState";
import { compareHuge, isHugeNumber, type HugeNumber } from "@/shared/numbers";
import {
  assertFiniteNonNegative,
  assertFinitePositive,
} from "./hoverIdleStateValidation";
import type { HovGainEffectSourceIntent } from "./hovGainEffectComposer";

// Effect adoption boundary for achievement persistent effects. Given the
// resolved effects and the current model state groups, this module returns
// the next state groups the HoverIdleModel should adopt. The applier owns
// the patch rules; the model keeps authoritative state ownership, lifecycle,
// and effect-sync timing.

export type HoverIdleAchievementPersistentEffects = Pick<
  AchievementPersistentEffects,
  | "hovGainFlatBonus"
  | "hovUpgradeCostMultiplier"
  | "hovCellUpgradeCostMultiplier"
  | "hovGainAchievementCountMultiplier"
  | "timGainAchievementCountMultiplier"
  | "hovGainAchievementMultiplier"
  | "h1gExponentBonus"
  | "canAssignMpToHovGainUpgrades"
  | "timHovActivationEfficiency"
  | "oneCostHoverRepeaterBatchEnabled"
> & {
  readonly hovGainFamilyUpgradeRepeatInitialIntervalMs?: number;
  readonly canAssignMpToAmplifierUpgrades?: boolean;
  readonly amplifierUpgradeRepeatInitialIntervalMs?: number;
  readonly amplifierGainFixedMultipliersByEntry?: AchievementPersistentEffects["amplifierGainFixedMultipliersByEntry"];
  readonly bhgToHgPowerExponentBonus?: number;
  readonly mhovMpExecutionUnlocked?: boolean;
  readonly amplifierPanelMpPlacementUnlocked?: boolean;
  // True when the achievement that upgrades the OHov HovCore reward
  // multiplier skill (so the multiplier scales with OHovState.power) is
  // unlocked.
  readonly ohovHovCoreGainScalingUnlocked?: boolean;
  // True when `outside-mhov` lifts the hard Number-limit cap on the MHov
  // reward. The post-boundary softcap remains active.
  readonly mhovRewardUncapped?: boolean;
  // True when `outside-nhov` lifts the Number-limit cap on all NHov branch
  // effects.
  readonly nhovBranchEffectUncapped?: boolean;
  readonly explorationTimAccelerationUnlocked?: boolean;
  readonly mpmPresetSlotBonus?: number;
};

export interface AchievementPersistentEffectBonuses {
  readonly hovGainFlatBonus: number;
  readonly hovUpgradeCostMultiplier: number;
  readonly hovCellUpgradeCostMultiplier: number;
  readonly hovGainCountMultiplier: number;
  readonly timGainCountMultiplier: number;
  readonly hovGainMultiplier: number;
  readonly h1gExponentBonus: number;
  readonly timHovActivationEfficiency: number;
  readonly oneCostHoverRepeaterBatchEnabled: boolean;
}

export interface AchievementAmplifierGainAdoption {
  readonly fixedMultipliersByEntry: ReadonlyMap<
    HovAmplifierEntryId,
    HugeNumber
  >;
}

export interface AchievementPersistentEffectApplierInput {
  readonly effects: HoverIdleAchievementPersistentEffects;
  readonly mpmState: MpmState;
  readonly mpCapacity: number;
  readonly mpCapacityInitialized: boolean;
}

export interface AchievementPersistentEffectAdoption {
  readonly bonuses: AchievementPersistentEffectBonuses;
  readonly hovGainIntent: HovGainEffectSourceIntent;
  readonly amplifier: AchievementAmplifierGainAdoption;
  readonly mpmUnlockIntent: MpmUnlockIntent;
  readonly mpmState: MpmState;
  readonly ohovHovCoreGainScalingUnlocked: boolean;
  readonly mhovRewardUncapped: boolean;
  readonly nhovBranchEffectUncapped: boolean;
  readonly explorationTimAccelerationUnlocked: boolean;
  readonly mpmPresetSlotBonus: number;
}

function validateEffects(effects: HoverIdleAchievementPersistentEffects): void {
  assertFiniteNonNegative(effects.hovGainFlatBonus, "effects.hovGainFlatBonus");
  assertFinitePositive(
    effects.hovUpgradeCostMultiplier,
    "effects.hovUpgradeCostMultiplier",
  );
  assertFinitePositive(
    effects.hovCellUpgradeCostMultiplier,
    "effects.hovCellUpgradeCostMultiplier",
  );
  assertFinitePositive(
    effects.hovGainAchievementCountMultiplier,
    "effects.hovGainAchievementCountMultiplier",
  );
  assertFiniteNonNegative(effects.h1gExponentBonus, "effects.h1gExponentBonus");
  if (effects.bhgToHgPowerExponentBonus !== undefined) {
    assertFiniteNonNegative(
      effects.bhgToHgPowerExponentBonus,
      "effects.bhgToHgPowerExponentBonus",
    );
  }
  assertFinitePositive(
    effects.hovGainAchievementMultiplier,
    "effects.hovGainAchievementMultiplier",
  );
  assertFinitePositive(
    effects.timHovActivationEfficiency,
    "effects.timHovActivationEfficiency",
  );
  validateAmplifierGainFixedMultipliers(
    effects.amplifierGainFixedMultipliersByEntry,
  );
  if (effects.mpmPresetSlotBonus !== undefined) {
    assertFiniteNonNegative(
      effects.mpmPresetSlotBonus,
      "effects.mpmPresetSlotBonus",
    );
    if (!Number.isInteger(effects.mpmPresetSlotBonus)) {
      throw new Error("effects.mpmPresetSlotBonus must be an integer");
    }
  }
}

function validateAmplifierGainFixedMultipliers(
  multipliers:
    | AchievementPersistentEffects["amplifierGainFixedMultipliersByEntry"]
    | undefined,
): void {
  for (const [entryId, multiplier] of multipliers ?? []) {
    if (
      !isHugeNumber(multiplier) ||
      !multiplier.isFinite() ||
      compareHuge(multiplier, 0) <= 0
    ) {
      throw new Error(
        `effects.amplifierGainFixedMultipliersByEntry.${entryId} must be a finite positive HugeNumber`,
      );
    }
  }
}

function resolveHovGainIntent(
  effects: HoverIdleAchievementPersistentEffects,
): HovGainEffectSourceIntent {
  return {
    bhgToHgPowerExponentBonus: effects.bhgToHgPowerExponentBonus ?? 0,
    hovGainCountMultiplier: effects.hovGainAchievementCountMultiplier,
    hovGainMultiplier: effects.hovGainAchievementMultiplier,
    h1gExponentBonus: effects.h1gExponentBonus,
  };
}

function resolveBonuses(
  effects: HoverIdleAchievementPersistentEffects,
): AchievementPersistentEffectBonuses {
  return {
    hovGainFlatBonus: effects.hovGainFlatBonus,
    hovUpgradeCostMultiplier: effects.hovUpgradeCostMultiplier,
    hovCellUpgradeCostMultiplier: effects.hovCellUpgradeCostMultiplier,
    hovGainCountMultiplier: effects.hovGainAchievementCountMultiplier,
    timGainCountMultiplier: effects.timGainAchievementCountMultiplier,
    hovGainMultiplier: effects.hovGainAchievementMultiplier,
    h1gExponentBonus: effects.h1gExponentBonus,
    timHovActivationEfficiency: effects.timHovActivationEfficiency,
    oneCostHoverRepeaterBatchEnabled: effects.oneCostHoverRepeaterBatchEnabled,
  };
}

function resolveMpmUnlockIntent(
  effects: HoverIdleAchievementPersistentEffects,
): MpmUnlockIntent {
  return {
    hovGainUpgradesUnlocked: effects.canAssignMpToHovGainUpgrades,
    mhovExecutionUnlocked: effects.mhovMpExecutionUnlocked === true,
    amplifierPanelsUnlocked: effects.amplifierPanelMpPlacementUnlocked === true,
    amplifierUpgradesUnlocked: effects.canAssignMpToAmplifierUpgrades === true,
    nhovExecutionUnlocked: false,
    ohovExecutionUnlocked: false,
  };
}

function resolveAmplifierAdoption(
  effects: HoverIdleAchievementPersistentEffects,
): AchievementAmplifierGainAdoption {
  return {
    fixedMultipliersByEntry: new Map(
      effects.amplifierGainFixedMultipliersByEntry ?? [],
    ),
  };
}

export function applyAchievementPersistentEffects(
  input: AchievementPersistentEffectApplierInput,
): AchievementPersistentEffectAdoption {
  validateEffects(input.effects);
  const mpmUnlockIntent = resolveMpmUnlockIntent(input.effects);
  return {
    bonuses: resolveBonuses(input.effects),
    hovGainIntent: resolveHovGainIntent(input.effects),
    amplifier: resolveAmplifierAdoption(input.effects),
    mpmUnlockIntent,
    mpmState: composeMpmUnlockAdoption({
      state: input.mpmState,
      sources: [mpmUnlockIntent],
      mpCapacity: input.mpCapacity,
      mpCapacityInitialized: input.mpCapacityInitialized,
    }),
    ohovHovCoreGainScalingUnlocked:
      input.effects.ohovHovCoreGainScalingUnlocked === true,
    mhovRewardUncapped: input.effects.mhovRewardUncapped === true,
    nhovBranchEffectUncapped: input.effects.nhovBranchEffectUncapped === true,
    explorationTimAccelerationUnlocked:
      input.effects.explorationTimAccelerationUnlocked === true,
    mpmPresetSlotBonus: input.effects.mpmPresetSlotBonus ?? 0,
  };
}
